import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { financialAPI, Account } from '../services/api';

const AccountManager: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [formData, setFormData] = useState({
    account_name: '',
    account_type: 'savings' as 'savings' | 'checking' | 'investment' | 'cash' | 'other',
    current_balance: '',
    description: '',
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const data = await financialAPI.getAccounts();
      setAccounts(data);
      setError(null);
    } catch (err) {
      setError('Failed to load accounts');
      console.error('Accounts load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (account?: Account) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        account_name: account.account_name,
        account_type: account.account_type,
        current_balance: account.current_balance.toString(),
        description: account.description,
      });
    } else {
      setEditingAccount(null);
      setFormData({
        account_name: '',
        account_type: 'savings',
        current_balance: '',
        description: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAccount(null);
  };

  const handleSubmit = async () => {
    try {
      const accountData = {
        account_name: formData.account_name,
        account_type: formData.account_type,
        current_balance: parseFloat(formData.current_balance),
        description: formData.description,
      };

      if (editingAccount) {
        await financialAPI.updateAccount({ ...accountData, id: editingAccount.id! });
      } else {
        await financialAPI.createAccount(accountData);
      }

      handleCloseDialog();
      loadAccounts();
    } catch (err) {
      console.error('Submit error:', err);
      setError('Failed to save account');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        await financialAPI.deleteAccount(id);
        loadAccounts();
      } catch (err) {
        console.error('Delete error:', err);
        setError('Failed to delete account');
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getTotalBalance = () => {
    return accounts.reduce((total, account) => total + account.current_balance, 0);
  };

  const getAccountTypeColor = (type: string) => {
    switch (type) {
      case 'savings':
        return 'success';
      case 'checking':
        return 'primary';
      case 'investment':
        return 'secondary';
      case 'cash':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getAccountsByType = () => {
    const groupedAccounts = accounts.reduce((acc, account) => {
      if (!acc[account.account_type]) {
        acc[account.account_type] = [];
      }
      acc[account.account_type].push(account);
      return acc;
    }, {} as Record<string, Account[]>);

    return groupedAccounts;
  };

  const getTypeTotal = (type: string) => {
    return accounts
      .filter(account => account.account_type === type)
      .reduce((total, account) => total + account.current_balance, 0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const accountsByType = getAccountsByType();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Account Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Account
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr 1fr 1fr' },
        gap: 2,
        mb: 4 
      }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Total Net Worth
            </Typography>
            <Typography variant="h4">
              {formatCurrency(getTotalBalance())}
            </Typography>
          </CardContent>
        </Card>

        {Object.keys(accountsByType).map((type) => (
          <Card key={type}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </Typography>
              <Typography variant="h5" color={`${getAccountTypeColor(type)}.main`}>
                {formatCurrency(getTypeTotal(type))}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Accounts Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Account Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Balance</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.map((account) => (
              <TableRow key={account.id}>
                <TableCell>{account.account_name}</TableCell>
                <TableCell>
                  <Chip
                    label={account.account_type}
                    color={getAccountTypeColor(account.account_type) as any}
                  />
                </TableCell>
                <TableCell>{formatCurrency(account.current_balance)}</TableCell>
                <TableCell>{account.description}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenDialog(account)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(account.id!)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAccount ? 'Edit Account' : 'Add New Account'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Account Name"
              value={formData.account_name}
              onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Account Type</InputLabel>
              <Select
                value={formData.account_type}
                label="Account Type"
                onChange={(e) => setFormData({ ...formData, account_type: e.target.value as any })}
              >
                <MenuItem value="savings">Savings</MenuItem>
                <MenuItem value="checking">Checking</MenuItem>
                <MenuItem value="investment">Investment</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              margin="normal"
              label="Current Balance"
              type="number"
              value={formData.current_balance}
              onChange={(e) => setFormData({ ...formData, current_balance: e.target.value })}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingAccount ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountManager;
